/* Left rail: workspace switcher, two zones.

   Top zone = primary workspaces. Bottom zone = Settings + the collapse toggle.
   Collapsed shows icons only, with labels falling back to the button's native
   title/aria-label rather than a bespoke tooltip component.

   Purely presentational: it renders the defs it's given and emits intent. It
   never imports a workspace or the router. */

import { LitElement, html, css, unsafeCSS, type PropertyValues } from 'lit';
import { shadowReset } from '../../design/base.js';
import { customElement, property } from 'lit/decorators.js';
import type { WorkspaceDef } from '../../core/types.js';
import '../../component/g-button/g-button.js';
import '../../component/g-icon/g-icon.js';

/** Width animation, shared by the stylesheet and the `compact` timer below so
    the two cannot drift apart. */
const COLLAPSE_MS = 140;

@customElement('g-rail')
export class GRail extends LitElement {
  static override styles = [shadowReset, css`
    :host {
      display: flex;
      flex-direction: column;
      width: var(--rail-w);
      flex: none;
      /* The rows' inset lives here, not in their padding: the button keeps a
         uniform pad (so collapsed it is an exact square) and the rail supplies
         the breathing room, which is the same on both sides in either state. */
      padding: var(--space-3);
      border-right: 1px solid var(--border);
      background: var(--bg);
      transition: width ${unsafeCSS(COLLAPSE_MS)}ms ease;
      overflow: hidden;
    }
    /* Collapsed is exactly one row plus this rail's own padding and border (the
       1px), so the square sits centred by construction and there is no second
       width to keep in sync with the row size. */
    :host([collapsed]) {
      width: calc(var(--rail-row-h) + var(--space-3) * 2 + 1px);
    }
    nav {
      display: flex;
      flex-direction: column;
      gap: var(--space-1);
    }
    .bottom {
      margin-top: auto;
      display: flex;
      flex-direction: column;
      gap: var(--space-1);
      padding-top: var(--space-2);
      border-top: 1px solid var(--border);
    }
    /* label hidden, not removed, so the accessible name survives collapse */
    .label {
      white-space: nowrap;
      overflow: hidden;
    }
    /* Keyed on compact, not collapsed: the label survives the whole shrink and
       is clipped away by the host's overflow, so the icon never moves. Dropping
       it on collapsed instead reflowed the row to icon-only one frame in, while
       the rail was still near full width — the icon shot 56px right and then
       chased the closing edge back. */
    :host([compact]) .label {
      display: none;
    }
    /* A row is one fixed height whether or not it shows its label, so collapsing
       is a pure width transition. g-button otherwise takes its height from the
       label's line box when labelled and from its own width (aspect-ratio) when
       icon-only — two unrelated sources, which made every row jump 2.5px and the
       top group shorten by 15px mid-animation. With a definite height here the
       icon-only square resolves its width FROM the height instead of the
       reverse, which is why it stays exactly 38x38. */
    g-button {
      display: flex;
      /* flex:none and min-height:0 are what make the height stick. These are
         column flex containers, so the main axis is vertical: g-button's own
         flex:1 plus the automatic content minimum would otherwise size the row
         and ignore the height outright. */
      flex: none;
      min-height: 0;
      height: var(--rail-row-h);
      /* One padding for both states, so the icon sits at the same x labelled or
         not and the switch at the end of the collapse moves nothing. Uniform
         rather than the default's wider horizontal pad, which also keeps the
         collapsed pill an exact square. */
      --g-button-pad: var(--space-2);
    }
  `];

  @property({ attribute: false }) items: WorkspaceDef[] = [];
  @property() active = '';
  @property({ type: Boolean, reflect: true }) collapsed = false;
  /**
   * Presentation, as opposed to `collapsed`, which is intent.
   *
   * It lags on the way in — the rail must finish narrowing before the rows drop
   * their labels — and leads on the way out, so the labels are already there for
   * the widening rail to reveal. Internal: reflected only so the stylesheet can
   * key off it, never set from outside.
   */
  @property({ type: Boolean, reflect: true }) compact = false;

  private compactTimer?: ReturnType<typeof setTimeout>;

  override willUpdate(changed: PropertyValues<this>): void {
    if (!changed.has('collapsed')) return;
    clearTimeout(this.compactTimer);
    // Expanding, or the very first render — nothing is animating, so no lag.
    if (!this.collapsed || changed.get('collapsed') === undefined) {
      this.compact = this.collapsed;
      return;
    }
    this.compactTimer = setTimeout(() => {
      this.compact = true;
    }, COLLAPSE_MS);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    clearTimeout(this.compactTimer);
  }

  private go(id: string) {
    this.dispatchEvent(new CustomEvent('navigate', { detail: id, bubbles: true, composed: true }));
  }

  private item(w: WorkspaceDef) {
    return html`
      <g-button
        full
        label=${w.label}
        ?selected=${w.id === this.active}
        ?icon-only=${this.compact}
        @click=${() => this.go(w.id)}
      >
        <g-icon name=${w.icon}></g-icon>
        <span class="label">${w.label}</span>
      </g-button>
    `;
  }

  override render() {
    const top = this.items.filter((w) => w.zone === 'top');
    const bottom = this.items.filter((w) => w.zone === 'bottom');
    return html`
      <nav aria-label="Workspaces">${top.map((w) => this.item(w))}</nav>
      <div class="bottom">
        ${bottom.map((w) => this.item(w))}
        <g-button
          full
          label=${this.collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          ?icon-only=${this.compact}
          @click=${() =>
            this.dispatchEvent(
              new CustomEvent('toggle-collapse', { bubbles: true, composed: true }),
            )}
        >
          <!-- the glyph follows compact so it doesn't flip mid-animation; the
               accessible name follows collapsed, which is the real state -->
          <g-icon name=${this.compact ? 'collapse-right' : 'collapse-left'}></g-icon>
          <span class="label">Collapse</span>
        </g-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-rail': GRail;
  }
}
